import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ToggleSwitch from "../../../ToggleSwitch";
import { formLabelClass, formControlClass } from "../../../dashboardStyles";

/**
 * Weekly schedule component with simple and advanced modes
 */
const WeeklySchedule = ({ resource, onUpdate }) => {
  const { t } = useTranslation();
  const updateWorkingDays = (day) => {
    const newWorkingDays = {
      ...resource.workingDays,
      [day]: !resource.workingDays[day],
    };
    onUpdate("workingDays", newWorkingDays);
  };

  const updateWorkingHours = (field, value) => {
    const newWorkingHours = {
      ...resource.workingHours,
      [field]: value,
    };
    onUpdate("workingHours", newWorkingHours);
  };

  const updateAdvancedSchedule = (day, field, value) => {
    const newSchedule = {
      ...resource.advancedSchedule,
      [day]: {
        ...resource.advancedSchedule[day],
        [field]: value,
      },
    };
    onUpdate("advancedSchedule", newSchedule);
  };

  // Scoped so two resources on the same step don't collide on element ids.
  const uid = resource.id ?? "resource";

  return (
    <div className="flex flex-col gap-4">
      <ToggleSwitch
        id={`advanced-schedule-${uid}`}
        checked={Boolean(resource.useAdvancedSchedule)}
        onChange={(e) => onUpdate("useAdvancedSchedule", e.target.checked)}
        label={
          resource.useAdvancedSchedule
            ? t("Dashboard.Wizard.weeklySchedule.differentHoursPerDay")
            : t("Dashboard.Wizard.weeklySchedule.sameHoursEveryDay")
        }
      />

      {!resource.useAdvancedSchedule ? (
        // Simple Weekly Schedule
        <div className="flex flex-col gap-4">
          <div>
            <span className={formLabelClass}>
              {t("Dashboard.Wizard.weeklySchedule.workingDays")}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(resource.workingDays).map((day) => (
                <button
                  key={day}
                  type="button"
                  aria-pressed={resource.workingDays[day]}
                  className={`min-w-11 cursor-pointer rounded-lg border px-2.5 py-2 text-xs font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600 ${
                    resource.workingDays[day]
                      ? "border-brand-600 bg-brand-600 text-white"
                      : "border-ink-200 bg-white text-ink-500 hover:border-brand-600 hover:text-brand-600"
                  }`}
                  onClick={() => updateWorkingDays(day)}
                >
                  {day.substring(0, 3).toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className={formLabelClass}>
              {t("Dashboard.Wizard.weeklySchedule.workingHours")}
            </span>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label
                  htmlFor={`start-${uid}`}
                  className="mb-1 block text-xs text-ink-500"
                >
                  {t("Dashboard.Wizard.weeklySchedule.startTime")}
                </label>
                <input
                  type="time"
                  id={`start-${uid}`}
                  value={resource.workingHours.start}
                  onChange={(e) => updateWorkingHours("start", e.target.value)}
                  className={`${formControlClass} w-auto`}
                />
              </div>
              <span className="pb-2.5 text-xs text-ink-400">
                {t("Dashboard.Wizard.weeklySchedule.to")}
              </span>
              <div>
                <label
                  htmlFor={`end-${uid}`}
                  className="mb-1 block text-xs text-ink-500"
                >
                  {t("Dashboard.Wizard.weeklySchedule.endTime")}
                </label>
                <input
                  type="time"
                  id={`end-${uid}`}
                  value={resource.workingHours.end}
                  onChange={(e) => updateWorkingHours("end", e.target.value)}
                  className={`${formControlClass} w-auto`}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Advanced Weekly Schedule
        <div>
          <span className={formLabelClass}>
            {t("Dashboard.Wizard.weeklySchedule.dailySchedule")}{" "}
            <span className="font-normal text-ink-400">
              {t("Dashboard.Wizard.weeklySchedule.dailyScheduleHint")}
            </span>
          </span>
          <div className="flex flex-col gap-2">
            {Object.keys(resource.advancedSchedule).map((day) => {
              const daySchedule = resource.advancedSchedule[day];
              return (
                <div
                  key={day}
                  className="flex flex-col gap-2 rounded-lg border border-ink-200 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <label
                    htmlFor={`${day}-enabled-${uid}`}
                    className="flex cursor-pointer items-center gap-2 select-none"
                  >
                    <input
                      type="checkbox"
                      id={`${day}-enabled-${uid}`}
                      checked={daySchedule.enabled}
                      onChange={(e) =>
                        updateAdvancedSchedule(day, "enabled", e.target.checked)
                      }
                      className="size-4 cursor-pointer accent-brand-600"
                    />
                    <span className="text-fluid-sm font-medium text-ink-900 capitalize">
                      {day}
                    </span>
                  </label>
                  {daySchedule.enabled && (
                    <div className="flex items-center gap-2">
                      <input
                        type="time"
                        aria-label={t(
                          "Dashboard.Wizard.weeklySchedule.dayStartTime",
                          { day },
                        )}
                        value={daySchedule.start}
                        onChange={(e) =>
                          updateAdvancedSchedule(day, "start", e.target.value)
                        }
                        className={`${formControlClass} w-auto py-1.5`}
                      />
                      <span className="text-ink-400">-</span>
                      <input
                        type="time"
                        aria-label={t(
                          "Dashboard.Wizard.weeklySchedule.dayEndTime",
                          { day },
                        )}
                        value={daySchedule.end}
                        onChange={(e) =>
                          updateAdvancedSchedule(day, "end", e.target.value)
                        }
                        className={`${formControlClass} w-auto py-1.5`}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

WeeklySchedule.propTypes = {
  resource: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default WeeklySchedule;
